import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Drawer,
  Empty,
  Input,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons'
import { adminApi, formatApiError, type PageResult } from '../api'
import type { KnowledgeScope } from '../constants/knowledgeScope'
import { tablePagination, useServerPagination } from './ui'

type ScopeParams = { scope: KnowledgeScope }

export type ChunkRow = {
  id: number
  item_id: number
  chunk_index: number
  content: string
  char_count: number
  chunk_level: string
  parent_content: string
  contextual_prefix: string
}

type ChunksDrawerProps = {
  open: boolean
  item: { id: number; title: string; chunk_count: number } | null
  scopeParams: ScopeParams
  onClose: () => void
}

function chunkParentVisible(chunk: ChunkRow): boolean {
  const parent = (chunk.parent_content || '').trim()
  const body = (chunk.content || '').trim()
  return Boolean(parent && parent !== body)
}

function ChunkDetailBody({ chunk }: { chunk: ChunkRow }) {
  return (
    <div className="kb-chunk-detail">
      {chunk.contextual_prefix ? (
        <Typography.Paragraph type="secondary" className="kb-chunk-detail-block">
          <strong>语境前缀：</strong>
          {chunk.contextual_prefix}
        </Typography.Paragraph>
      ) : null}
      {chunkParentVisible(chunk) ? (
        <div className="kb-chunk-detail-block">
          <Typography.Text strong>父段</Typography.Text>
          <pre className="kb-chunk-pre kb-chunk-pre--drawer">{chunk.parent_content}</pre>
        </div>
      ) : null}
      <div className="kb-chunk-detail-block">
        <Typography.Text strong>正文</Typography.Text>
        <pre className="kb-chunk-pre kb-chunk-pre--drawer">{chunk.content}</pre>
      </div>
    </div>
  )
}

export function KnowledgeChunksDrawer({ open, item, scopeParams, onClose }: ChunksDrawerProps) {
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<ChunkRow[]>([])
  const [total, setTotal] = useState(0)
  const { page, pageSize, handlePaginationChange, paginationOptions, resetPage } = useServerPagination()
  const [loadError, setLoadError] = useState<string | null>(null)
  const [viewChunk, setViewChunk] = useState<ChunkRow | null>(null)
  const [editChunk, setEditChunk] = useState<ChunkRow | null>(null)
  const [editContent, setEditContent] = useState('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => {
    if (!item) return
    setLoading(true)
    setLoadError(null)
    adminApi
      .get<PageResult<ChunkRow>>(`/admin/knowledge/items/${item.id}/chunks`, {
        params: { ...scopeParams, page, page_size: pageSize },
      })
      .then((r) => {
        setRows(r.data.items)
        setTotal(r.data.total)
      })
      .catch((e) => {
        const msg = formatApiError(e, '加载切片失败')
        setLoadError(msg)
        setRows([])
        setTotal(0)
        message.error(msg, 8)
      })
      .finally(() => setLoading(false))
  }, [item, scopeParams, page, pageSize])

  useEffect(() => {
    if (open && item) {
      resetPage()
      setViewChunk(null)
      setEditChunk(null)
    }
    if (!open) {
      setRows([])
      setTotal(0)
      setLoadError(null)
      setViewChunk(null)
      setEditChunk(null)
    }
  }, [open, item?.id])

  useEffect(() => {
    if (open && item) load()
  }, [open, item, load])

  const openEdit = (c: ChunkRow) => {
    setViewChunk(null)
    setEditChunk(c)
    setEditContent(c.content)
  }

  const openView = (c: ChunkRow) => {
    setEditChunk(null)
    setViewChunk(c)
  }

  const backToList = () => {
    setViewChunk(null)
    setEditChunk(null)
  }

  const saveChunk = () => {
    if (!item || !editChunk) return
    const text = editContent.trim()
    if (!text) {
      message.warning('切片内容不能为空')
      return
    }
    setSaving(true)
    adminApi
      .put<ChunkRow>(
        `/admin/knowledge/items/${item.id}/chunks/${editChunk.chunk_index}`,
        { content: text },
        { params: scopeParams },
      )
      .then(() => {
        message.success('切片已更新')
        setEditChunk(null)
        load()
      })
      .catch((e) => message.error(e.message))
      .finally(() => setSaving(false))
  }

  const drawerTitle = (() => {
    if (!item) return '查看切片'
    if (viewChunk) return `切片 #${viewChunk.chunk_index + 1} · ${item.title}`
    if (editChunk) return `编辑切片 #${editChunk.chunk_index + 1} · ${item.title}`
    const countLabel = total > 0 ? `（共 ${total} 条）` : ''
    return `查看切片：${item.title}${countLabel}`
  })()

  return (
    <Drawer
      title={drawerTitle}
      open={open}
      onClose={() => {
        backToList()
        onClose()
      }}
      width={800}
      destroyOnHidden
      extra={
        viewChunk || editChunk ? (
          <Button type="link" icon={<ArrowLeftOutlined />} onClick={backToList}>
            返回列表
          </Button>
        ) : null
      }
    >
      <Spin spinning={loading}>
        {viewChunk ? (
          <ChunkDetailBody chunk={viewChunk} />
        ) : editChunk ? (
          <div className="kb-chunk-edit">
            <Typography.Paragraph type="secondary">
              仅修改本切片正文；保存后会更新向量。大批量调整建议改「文档详情」中的全文后重新索引。
            </Typography.Paragraph>
            <Input.TextArea
              rows={18}
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
            />
            <Space style={{ marginTop: 16 }}>
              <Button type="primary" loading={saving} onClick={() => void saveChunk()}>
                保存
              </Button>
              <Button onClick={backToList}>取消</Button>
            </Space>
          </div>
        ) : rows.length === 0 && !loading ? (
          <Empty description={loadError || '暂无切片，请先点击「索引」或确认 ES 已连接'} />
        ) : (
          <Table
            rowKey="id"
            size="small"
            dataSource={rows}
            pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
            columns={[
              {
                title: '#',
                width: 56,
                render: (_, r) => r.chunk_index + 1,
              },
              {
                title: '层级',
                dataIndex: 'chunk_level',
                width: 72,
                render: (v: string) => (v === 'parent' ? <Tag>父</Tag> : <Tag color="blue">子</Tag>),
              },
              { title: '字数', dataIndex: 'char_count', width: 64 },
              {
                title: '内容摘要',
                ellipsis: true,
                render: (_, r) => (
                  <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ margin: 0 }}>
                    {r.content || '—'}
                  </Typography.Paragraph>
                ),
              },
              {
                title: '操作',
                width: 120,
                render: (_, r) => (
                  <Space>
                    <Button size="small" onClick={() => openView(r)}>
                      查看
                    </Button>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
                      改
                    </Button>
                  </Space>
                ),
              },
            ]}
          />
        )}
      </Spin>
    </Drawer>
  )
}
